import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  MicroreelGroup,
  MicroreelLesson,
  getGroupReels,
} from '../../../../data/models/Microreels/MicroreelGroup';
import {LessonPresentation} from '../../../../data/models/Course/LessonPresentation';
import {microreelsListText} from '../microreelsListText';
import {formatTimeAgo} from '../../../../utils/notificationTimeTextConvertor';
import {
  formatReelDuration,
  formatReelDurationLong,
  getMicroreelAckNode,
  getMicroreelQuestion,
  ReelGenerationMeta,
  ReelMeta,
} from '../reelMeta';
import {LANGUAGE_OPTIONS} from '../../../../data/models/AI/VideoGenerationOptions';
import {
  LessonContentMChoice,
  LessonContentMChoiceAnswer,
} from '../../../../data/models/LessonContent/LessonContentBase';
import MicroreelQuestionEditor from '../components/MicroreelQuestionEditor';
import {readGroupSettings} from '../../Generating/generatingTypes';
import {generatingMicroreelText} from '../../Generating/generatingMicroreelText';
import {generateMicroreelText} from '../../../TapyblAI/GenerateMicroreel/generateMicroreelText';
import MicroreelPhonePreview from '../components/MicroreelPhonePreview';
import ModalFade, {useModalFade} from '../components/ModalFade';
import ImageWithFallback from '../../../../uiToolkit/ImageWithFallback/ImageWithFallback';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import styles from './MicroreelsGroupView.module.css';

interface Props {
  group: MicroreelGroup;
  selectedReel?: MicroreelLesson;
  presentation?: LessonPresentation;
  reelMetas: {[reelId: string]: ReelMeta};
  reelAckFlags: {[reelId: string]: boolean};
  reelGenMeta: {[reelId: string]: ReelGenerationMeta};
  isPreviewLoading: boolean;
  onSelectReel: (reel: MicroreelLesson) => void;
  onBack: () => void;
  backLabel?: string;
  onLearners: () => void;
  onAnalytics: () => void;
  onEditGroupInfo: () => void;
  onAddToCourse: () => void;
  isPublishing: boolean;
  onEditVideo: (reel: MicroreelLesson) => void;
  isReelVideoEditable: (reel: MicroreelLesson) => boolean;
  onEditInfo: (reelId: string) => void;
  onEditQuestion: (reel: MicroreelLesson) => void;
  onDelete: (reelId: string) => void;
  onMoveReel: (reel: MicroreelLesson, direction: 1 | -1) => void;
  questionEditReel?: MicroreelLesson;
  questionEditNode?: LessonContentMChoice;
  isQuestionSaving: boolean;
  onSaveQuestion: (
    question: string,
    answers: LessonContentMChoiceAnswer[],
  ) => void;
  onCloseQuestion: () => void;
}

// Reel durations are hidden for now; flip to bring back the per-reel
// "Duration: m:ss" line and the "N min in total" subtitle part.
const SHOW_REEL_DURATIONS = false;

// Details card (title, description and the View Details fields) is hidden
// for now; flip to bring it back.
const SHOW_DETAILS_SECTION = false;

const ChecklistIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
    <path
      d="M2 5.2L3.6 6.8L6.4 3.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 5.2H16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M2 12.2L3.6 13.8L6.4 10.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12.2H16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ScissorsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
    <circle cx="4.2" cy="4.8" r="2.2" stroke="currentColor" strokeWidth="1.4" />
    <circle
      cx="4.2"
      cy="13.2"
      r="2.2"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M6.1 6.1L15.6 15M6.1 11.9L15.6 3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const MicroreelsGroupView = React.memo(
  ({
    group,
    selectedReel,
    presentation,
    reelMetas,
    reelAckFlags,
    reelGenMeta,
    isPreviewLoading,
    onSelectReel,
    onBack,
    backLabel,
    onLearners,
    onAnalytics,
    onEditGroupInfo,
    onAddToCourse,
    isPublishing,
    onEditVideo,
    isReelVideoEditable,
    onEditInfo,
    onEditQuestion,
    onDelete,
    onMoveReel,
    questionEditReel,
    questionEditNode,
    isQuestionSaving,
    onSaveQuestion,
    onCloseQuestion,
  }: Props) => {
    const reels = getGroupReels(group);
    const [progress, setProgress] = useState(0);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const previewFade = useModalFade(previewMode);

    useEffect(() => {
      if (!previewMode) return;
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') {
          setPreviewMode(false);
        }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [previewMode]);

    // The modal is portaled to document.body, so the whole app root (top bar
    // and side menu included) can be blurred behind it.
    useEffect(() => {
      const root = document.getElementById('root');
      if (!root) return;
      root.style.transition = 'filter 0.2s ease';
      root.style.filter = previewMode ? 'blur(6px)' : '';
      return () => {
        root.style.filter = '';
        root.style.transition = '';
      };
    }, [previewMode]);

    const metas = reels.map(reel => reelMetas[reel.id]);
    const totalDurationSeconds =
      metas.length && metas.every(Boolean)
        ? metas.reduce((sum, meta) => sum + meta.durationSeconds, 0)
        : undefined;
    // Generation settings come from the local per-group snapshot when this
    // browser generated the group; otherwise the backend now copies
    // language/dialect/gender onto each reel lesson, and only groups older
    // than that change fall back to the generation defaults.
    const groupSettings = readGroupSettings(group.id);
    const backendGen = reels
      .map(reel => reelGenMeta[reel.id])
      .find(meta => meta && (meta.language || meta.gender));
    // The dialect is only trusted alongside a gender: video-source jobs store
    // a hardcoded placeholder dialect ("GB") with no gender, and there the
    // language is on-screen text only — no dialect was ever chosen.
    const backendLanguageLabel = backendGen?.language
      ? `${
          LANGUAGE_OPTIONS.find(option => option.key === backendGen.language)
            ?.value ?? backendGen.language
        }${
          backendGen.dialect && backendGen.gender
            ? ` ${backendGen.dialect}`
            : ''
        }`
      : undefined;
    // gender is null on video-source reels (original audio) and pre-change
    // groups, where the voice cannot be derived.
    const backendVoiceName =
      backendGen?.gender === 'female'
        ? generateMicroreelText.voiceFemaleName
        : backendGen?.gender === 'male'
        ? generateMicroreelText.voiceMaleName
        : undefined;
    const backendVoiceHint =
      backendGen?.gender === 'female'
        ? generateMicroreelText.voiceFemaleDetailsHint
        : backendGen?.gender === 'male'
        ? generateMicroreelText.voiceMaleDetailsHint
        : undefined;
    // The pipeline generates in-reel questions regardless of the chosen end
    // action; when the user picked None or the attestation, those questions
    // must not surface (preview or editor).
    const questionsDisabled =
      !!groupSettings?.endActionKey &&
      groupSettings.endActionKey !== 'assessment';
    const detailFields: {label: string; value: string; hint?: string}[] =
      groupSettings
        ? [
            {
              label: generatingMicroreelText.labelLength,
              value: groupSettings.lengthLabel,
              hint: generatingMicroreelText.hintLength,
            },
            {
              label: generatingMicroreelText.labelLanguage,
              value: backendLanguageLabel ?? groupSettings.languageLabel,
              // Snapshots from video generations made before languageHint
              // existed are recognized by their "Original audio" voice.
              hint:
                groupSettings.languageHint ??
                (groupSettings.voiceName === generateMicroreelText.voiceOriginal
                  ? generateMicroreelText.languageOnScreenHint
                  : generatingMicroreelText.hintLanguage),
            },
            // The lessons carry the real generation settings, so they beat
            // the local snapshot; video-source reels have no gender (original
            // audio) and fall back to the snapshot's "Original audio".
            {
              label: generatingMicroreelText.labelVoice,
              value: backendVoiceName ?? groupSettings.voiceName,
              hint:
                backendVoiceHint ??
                groupSettings.voiceHint ??
                generatingMicroreelText.hintVoice,
            },
            ...(groupSettings.visualsLabel
              ? [
                  {
                    label: generatingMicroreelText.labelVisuals,
                    value: groupSettings.visualsLabel,
                    hint: groupSettings.visualsHint,
                  },
                ]
              : []),
            // ...(groupSettings.sourceFileName
            //   ? [
            //       {
            //         label: generatingMicroreelText.source,
            //         value: groupSettings.sourceFileName,
            //         hint: `${
            //           groupSettings.sourcePages
            //             ? `${groupSettings.sourcePages} ${generatingMicroreelText.pages} · `
            //             : ''
            //         }${groupSettings.sourceFileSizeMb} MB`,
            //       },
            //     ]
            //   : []),
          ]
        : [
            {
              label: generatingMicroreelText.labelLength,
              value: generatingMicroreelText.valueLength,
              hint: generatingMicroreelText.hintLength,
            },
            {
              label: generatingMicroreelText.labelLanguage,
              value:
                backendLanguageLabel ?? generatingMicroreelText.valueLanguage,
              hint: generatingMicroreelText.hintLanguage,
            },
            {
              label: generatingMicroreelText.labelVoice,
              value: backendVoiceName ?? generatingMicroreelText.valueVoice,
              hint: backendVoiceHint ?? generatingMicroreelText.hintVoice,
            },
            {
              label: generatingMicroreelText.labelVisuals,
              value: generatingMicroreelText.valueVisuals,
              hint: generatingMicroreelText.hintVisuals,
            },
          ];

    const reelsSubtitleParts = [
      SHOW_REEL_DURATIONS && totalDurationSeconds !== undefined
        ? microreelsListText.totalDurationLong(
            formatReelDurationLong(totalDurationSeconds),
          )
        : undefined,
      group.dateUpdated
        ? microreelsListText.editedAgo(formatTimeAgo(group.dateUpdated))
        : undefined,
    ].filter((part): part is string => !!part);

    const onProgress = useCallback((fraction: number) => {
      setProgress(Math.round(fraction * 100));
    }, []);

    // The preview walks the whole course, so it always starts from reel 1.
    const onTogglePreview = () => {
      if (previewMode) {
        setPreviewMode(false);
        return;
      }
      if (reels.length) {
        onSelectReel(reels[0]);
      }
      setPreviewMode(true);
    };

    useEffect(() => {
      setProgress(0);
    }, [selectedReel?.id]);

    const renderReelRow = (reel: MicroreelLesson, index: number) => {
      const selected = selectedReel?.id === reel.id;
      return (
        <div
          key={reel.id}
          className={`${styles.reelRow} ${
            selected ? styles.reelRowSelected : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => onSelectReel(reel)}
          onKeyDown={ev => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              onSelectReel(reel);
            }
          }}>
          <div
            className={styles.reorderColumn}
            onClick={ev => ev.stopPropagation()}>
            <button
              className={styles.reorderButton}
              onClick={() => onMoveReel(reel, -1)}
              disabled={index === 0}
              title={microreelsListText.moveUp}>
              ˄
            </button>
            <span className={styles.reorderNumber}>{index + 1}</span>
            <button
              className={styles.reorderButton}
              onClick={() => onMoveReel(reel, 1)}
              disabled={index === reels.length - 1}
              title={microreelsListText.moveDown}>
              ˅
            </button>
          </div>
          <div className={styles.reelThumbnail}>
            {reel.poster ? (
              <ImageWithFallback
                src={reel.poster}
                className={styles.reelImage}
              />
            ) : (
              <Icon name="microreel.svg" className={styles.reelFallbackIcon} />
            )}
            {!selected && (
              <span className={styles.reelPlayBadge}>
                <Icon name="play_ic.svg" className={styles.reelPlayIcon} />
              </span>
            )}
            {selected && (
              <span
                className={styles.reelProgressBar}
                style={{width: `${progress}%`}}
              />
            )}
          </div>
          <div className={styles.reelInfo}>
            <p className={styles.reelTitle}>{reel.title}</p>
            {reel.description && (
              <p className={styles.reelDescription}>{reel.description}</p>
            )}
            {SHOW_REEL_DURATIONS && reelMetas[reel.id] && (
              <p className={styles.reelMetaLine}>
                {microreelsListText.reelDuration(
                  formatReelDuration(reelMetas[reel.id].durationSeconds),
                )}
              </p>
            )}
            {selected && (
              <span className={styles.nowPlayingChip}>
                <span className={styles.equalizer}>
                  <span className={styles.equalizerBar} />
                  <span className={styles.equalizerBar} />
                  <span className={styles.equalizerBar} />
                </span>
                {microreelsListText.nowPlaying}
              </span>
            )}
          </div>
          <div
            className={styles.reelActions}
            onClick={ev => ev.stopPropagation()}>
            <button
              className={styles.reelActionButton}
              onClick={() => onEditInfo(reel.id)}
              title={microreelsListText.editInfo}>
              <span className="icon-edit" />
            </button>
            {(() => {
              // Uploaded source videos have no chunks for the video editor,
              // so those reels open in the course editor instead.
              const videoEditable = isReelVideoEditable(reel);
              return (
                <button
                  className={styles.reelActionButton}
                  onClick={() => onEditVideo(reel)}
                  title={
                    videoEditable
                      ? microreelsListText.editVideo
                      : microreelsListText.editVideoInCourseEditor
                  }>
                  <ScissorsIcon />
                </button>
              );
            })()}
            {(() => {
              // No editable question: the chosen end action was None or the
              // attestation, or the reel's content simply has no questions.
              const noActions =
                questionsDisabled ||
                !!reelAckFlags[reel.id] ||
                (reelMetas[reel.id] !== undefined &&
                  reelMetas[reel.id].questionCount === 0);
              return (
                <button
                  className={styles.reelActionButton}
                  onClick={noActions ? undefined : () => onEditQuestion(reel)}
                  disabled={noActions}
                  title={
                    noActions
                      ? microreelsListText.noActionsTooltip
                      : microreelsListText.editQuestionTitle
                  }>
                  <ChecklistIcon />
                </button>
              );
            })()}
            <button
              className={`${styles.reelActionButton} ${styles.reelActionDanger}`}
              onClick={() => onDelete(reel.id)}
              title={microreelsListText.delete}>
              <span className="icon-trash" />
            </button>
          </div>
        </div>
      );
    };

    return (
      <div className={styles.container}>
        <button className={styles.backLink} onClick={onBack}>
          ← {backLabel ?? microreelsListText.allMicroreels}
        </button>
        <div className={styles.headerRow}>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{group.title}</h1>
          </div>
          <div className={styles.actions}>
            <button className={styles.textAction} onClick={onTogglePreview}>
              {microreelsListText.preview}
            </button>
            <button className={styles.textAction} onClick={onEditGroupInfo}>
              {microreelsListText.edit}
            </button>
            <button
              className={styles.textAction}
              onClick={onAnalytics}
              disabled={!group.publishedCourseId}
              title={
                group.publishedCourseId
                  ? undefined
                  : microreelsListText.analyticsNeedPublish
              }>
              {microreelsListText.analytics}
            </button>
            <span className={styles.actionsDivider} />
            <button className={styles.outlineAction} onClick={onAddToCourse}>
              {microreelsListText.addToCourse}
            </button>
            <button
              className={styles.shareButton}
              onClick={onLearners}
              disabled={isPublishing}>
              {microreelsListText.shareWithLearners}
            </button>
          </div>
        </div>

        {questionEditReel && questionEditNode ? (
          <MicroreelQuestionEditor
            key={questionEditReel.id}
            reelTitle={questionEditReel.title}
            reelIndex={
              reels.findIndex(reel => reel.id === questionEditReel.id) + 1
            }
            initialQuestion={questionEditNode.question}
            initialAnswers={questionEditNode.answers ?? []}
            durationSeconds={
              SHOW_REEL_DURATIONS
                ? reelMetas[questionEditReel.id]?.durationSeconds
                : undefined
            }
            isSaving={isQuestionSaving}
            onCancel={onCloseQuestion}
            onSave={onSaveQuestion}
          />
        ) : (
          <div className={styles.body}>
            <div className={styles.preview}>
              {previewFade.rendered ? (
                <div className={styles.phoneGhost} />
              ) : (
                <MicroreelPhonePreview
                  groupTitle={group.title}
                  reels={reels}
                  selectedReel={selectedReel}
                  presentation={presentation}
                  isLoading={isPreviewLoading}
                  onSelectReel={onSelectReel}
                  onProgress={onProgress}
                />
              )}
            </div>

            <div className={styles.rightColumn}>
              {SHOW_DETAILS_SECTION && (
                <div className={styles.detailsSection}>
                  <h2 className={styles.detailsHeading}>
                    {microreelsListText.details}
                  </h2>
                  {group.subjectTitle && (
                    <p className={styles.detailsDescription}>
                      {group.subjectTitle}
                    </p>
                  )}
                  <div className={styles.detailsDivider} />
                  <button
                    className={styles.viewDetailsRow}
                    onClick={() => setDetailsOpen(open => !open)}>
                    <span>{microreelsListText.viewDetails}</span>
                    <svg
                      className={`${styles.detailsChevron} ${
                        detailsOpen ? styles.detailsChevronOpen : ''
                      }`}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden>
                      <path
                        d="M3 5.5L8 10.5L13 5.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {detailsOpen && (
                    <div className={styles.detailsGrid}>
                      {detailFields.map(field => (
                        <div key={field.label} className={styles.detailsField}>
                          <span className={styles.detailsLabel}>
                            {field.label}
                          </span>
                          <span className={styles.detailsValue}>
                            {field.value}
                          </span>
                          {field.hint && (
                            <span className={styles.detailsHint}>
                              {field.hint}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className={styles.reelsHeadingRow}>
                <div>
                  <h2 className={styles.reelsHeading}>
                    {microreelsListText.reelsCount(reels.length)}
                  </h2>
                  {reelsSubtitleParts.length > 0 && (
                    <p className={styles.reelsSubtitle}>
                      {reelsSubtitleParts.join('  ·  ')}
                    </p>
                  )}
                </div>
                <span className={styles.reelsHint}>
                  {microreelsListText.dragOrderHint}
                </span>
              </div>

              <div className={styles.reelsList}>
                {reels.map((reel, index) => renderReelRow(reel, index))}
              </div>

              <div className={styles.ctaCard}>
                <div className={styles.ctaText}>
                  <p className={styles.ctaTitle}>
                    {microreelsListText.getInFrontTitle}
                  </p>
                  <p className={styles.ctaHint}>
                    {microreelsListText.getInFrontHint}
                  </p>
                </div>
                <div className={styles.ctaActions}>
                  <button
                    className={styles.ctaOutlineButton}
                    onClick={onAddToCourse}>
                    {microreelsListText.addToCourse}
                  </button>
                  <button
                    className={styles.ctaDarkButton}
                    onClick={onLearners}
                    disabled={isPublishing}>
                    {microreelsListText.shareWithLearners}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {previewFade.rendered &&
          createPortal(
            <ModalFade closing={previewFade.closing}>
              <div
                className={styles.previewOverlay}
                onClick={() => setPreviewMode(false)}>
                <button
                  className={styles.previewClose}
                  onClick={() => setPreviewMode(false)}
                  aria-label={microreelsListText.exitPreview}>
                  ✕
                </button>
                <div
                  className={styles.previewPhoneWrap}
                  onClick={ev => ev.stopPropagation()}>
                  <MicroreelPhonePreview
                    groupTitle={group.title}
                    reels={reels}
                    selectedReel={selectedReel}
                    presentation={presentation}
                    isLoading={isPreviewLoading}
                    onSelectReel={onSelectReel}
                    onProgress={onProgress}
                    learnerMode
                    question={
                      questionsDisabled
                        ? undefined
                        : getMicroreelQuestion(presentation?.lessonContent)
                    }
                    ackNode={getMicroreelAckNode(presentation?.lessonContent)}
                    ackFlag={
                      selectedReel ? !!reelAckFlags[selectedReel.id] : false
                    }
                    ackLanguage={
                      (selectedReel &&
                        reelGenMeta[selectedReel.id]?.language) ||
                      groupSettings?.languageKey
                    }
                    onExitPreview={() => setPreviewMode(false)}
                  />
                </div>
              </div>
            </ModalFade>,
            document.body,
          )}
      </div>
    );
  },
);

export default MicroreelsGroupView;
